import { responsibleTradeComplete } from '../domain/responsibleTrade';
import { itemLimitationsIncomplete, itemResultIncomplete, progressFor, type ProgressSummary, type V2Area, type V2ChecklistItem } from '../domain/inspection';
import type { V2Finding } from '../domain/finding';
import { observationBelongsToRow } from '../domain/aiObservation';
import type { WordingSource } from '../domain/wordingGuard';
import { containsChinese, reportBoundTextOf } from '../report/boundText';
import { rowSentenceFor, storedSentenceCurrent, unsyncedWordingLanguage, wordingBasisOf } from '../report/rowWording';
import type { AiState } from './aiStore';
import type { EvidenceState } from './evidenceStore';
import type { FindingState } from './findingStore';
import type { InspectionState } from './inspectionStore';

// Closeout (Owner task book 2026-09-04 §17): a precise gap list, not a legal audit page. Only what
// is genuinely unfinished is listed:
//   uninspected              an item of an enabled area still has no result           (blocking)
//   unableNoReason           Unable to Inspect without a reason                        (blocking)
//   unableNoNote             Unable to Inspect with reason "Other" and no written      (blocking — NY F1 Round 3,
//                            reason                                                     Owner decision OD-7 = C)
//   portionNoNote            a limited portion with reason "Other" and no written      (blocking — OD-5 / OD-7)
//                            reason
//   defectNoFacts            a Defect row with no fact the report could be written     (blocking — Final Owner
//                            from: no inspector note, no AI observation on the row,     Decision 2026-09-14 D-CA-2)
//                            no report sentence
//   defectNoWording          a Defect row whose report sentence is still the library   (advisory — the AI or the
//                            fallback (not written by the AI or the inspector)          inspector should write it)
//   wordingEnglishNotSynced  the inspector rewrote a row's sentence in CHINESE and the  (blocking — Owner device test
//                            English the client reads still says what stood before      2026-09-21: the delivered English
//                                                                                       must say what the inspector wrote)
//   findingNoRecommendation  a saved other finding still has no recommendation         (blocking)
//   findingNoTrade           a saved defect / safety other finding has no responsible  (blocking — the delivered
//                            trade                                                      report must name the trade)
//   findingNoPhoto           a saved other finding without a photo                     (advisory only)
//   clientEnglishChinese     the English client report would print Chinese the         (blocking — E2E walkthrough
//                            inspector wrote: a limitation / not-present reason, a      2026-10-16: the English PDF
//                            descriptor, a note, a name, a caption, or a row sentence   for an American client carried
//                            built from a Chinese note                                  the inspector's Chinese)
// AI observations never block (§17). Disabled areas are out of scope. Drafts are not in the report
// and never block on their own.
export type CloseoutGapKind =
  | 'uninspected'
  | 'unableNoReason'
  | 'unableNoNote'
  | 'portionNoNote'
  | 'defectNoFacts'
  | 'defectNoWording'
  | 'wordingEnglishNotSynced'
  | 'clientEnglishChinese'
  | 'findingNoRecommendation'
  | 'findingNoTrade'
  | 'findingNoPhoto';
// (2026-10-19 … 10-26 there was a 'photosUnconfirmed' advisory gap for AI-proposed photos; since 2026-10-27
// an AI placement is a link and reaches the report, so there is nothing left to confirm.)

export interface CloseoutGap {
  kind: CloseoutGapKind;
  blocking: boolean;
  // null = the inspection itself (its notes, its weather), not one of its areas
  areaId: string | null;
  areaName: string | null;
  itemId: string | null;
  itemTitle: string | null;
  findingId: string | null;
  findingTitle: string | null;
  // clientEnglishChinese: where the text prints and the text itself, so the inspector sees what to rewrite
  source: WordingSource | null;
  text: string | null;
}

export interface CloseoutSummary {
  gaps: CloseoutGap[];
  blocking: CloseoutGap[];
  advisory: CloseoutGap[];
  progress: ProgressSummary;
  enabledAreas: number;
  canFinalize: boolean;
}

// The facts the row's report wording is written from, as the closeout and the report see them
// (CA-14): the row's note, its photo notes, its AI observations and the advanced review summary.
export interface RowFactsInput {
  aiObservations: (itemId: string) => readonly string[];
  advancedReview: (itemId: string) => string | null;
  photoNotes: (item: V2ChecklistItem) => readonly string[];
  // the caption of any photo (a finding's photos print with their captions); absent = no captions known
  captionOf?: (evidenceId: string) => string;
}

export function rowFactsFrom(ai: Pick<AiState, 'observations'>, evidence: Pick<EvidenceState, 'evidence'>, advancedSummaries: ReadonlyMap<string, string> = new Map()): RowFactsInput {
  const captionOf = (id: string) => evidence.evidence.find((e) => e.id === id)?.caption ?? '';
  return {
    aiObservations: (itemId) =>
      ai.observations.filter((o) => o.itemId === itemId && observationBelongsToRow(o)).map((o) => (o.observation.trim().length > 0 ? o.observation : o.headline)),
    advancedReview: (itemId) => advancedSummaries.get(itemId) ?? null,
    photoNotes: (item) => item.evidenceIds.map((id) => captionOf(id).trim()).filter((c) => c.length > 0),
    captionOf
  };
}

const NO_FACTS: RowFactsInput = { aiObservations: () => [], advancedReview: () => null, photoNotes: () => [] };

// A Defect row has a fact source when the inspector wrote a note, an AI observation sits on the row,
// or a report sentence already stands (D-CA-2).
export function defectRowHasFacts(item: V2ChecklistItem, facts: RowFactsInput): boolean {
  if (item.inspectorNote.trim().length > 0) return true;
  if (facts.aiObservations(item.id).length > 0) return true;
  return item.reportSentence !== null && (item.reportSentence.en.trim().length > 0 || item.reportSentence.zh.trim().length > 0);
}

function basisOf(item: V2ChecklistItem, facts: RowFactsInput): string {
  return wordingBasisOf(item, { photoNotes: facts.photoNotes(item), aiObservations: facts.aiObservations(item.id), advancedReview: facts.advancedReview(item.id) });
}

export function defectRowWordingWritten(item: V2ChecklistItem, facts: RowFactsInput): boolean {
  return storedSentenceCurrent(item, basisOf(item, facts));
}

// The inspector's last word on the row is Chinese and the English was not brought in line with it.
export function rowEnglishWordingNotSynced(item: V2ChecklistItem, facts: RowFactsInput): boolean {
  if (item.status !== 'checkedOk' && item.status !== 'issueFound') return false;
  return unsyncedWordingLanguage(item, basisOf(item, facts)) === 'en';
}

function gap(kind: CloseoutGapKind, blocking: boolean, area: V2Area | null, item: V2ChecklistItem | null, finding: V2Finding | null, source: WordingSource | null = null, text: string | null = null): CloseoutGap {
  return {
    kind,
    blocking,
    areaId: area ? area.id : null,
    areaName: area ? area.name : null,
    itemId: item ? item.id : null,
    itemTitle: item ? item.title : null,
    findingId: finding ? finding.id : null,
    findingTitle: finding ? finding.title : null,
    source,
    text
  };
}

// Everything the English client report prints from the inspector's own words, in Chinese. Three
// sources: the enumeration the wording reminder reads (src/report/boundText.ts — notes, weather,
// names, reasons, portions, descriptors, finding text and captions); each observed row's English
// sentence as the report builds it (a defect with no English written prints the inspector's note);
// and the photo notes of a defect row, which print under its photos. The area name inside a row
// sentence is checked once, as the area's own name, not again in every row of the area.
function clientEnglishChineseGaps(inspectionId: string, inspection: InspectionState, findings: FindingState, facts: RowFactsInput, areas: readonly V2Area[], items: readonly V2ChecklistItem[], alreadyUnsynced: ReadonlySet<string>): CloseoutGap[] {
  const out: CloseoutGap[] = [];
  const seen = new Set<string>();
  const areaById = new Map(areas.map((a) => [a.id, a] as const));
  const itemById = new Map(items.map((i) => [i.id, i] as const));
  const findingById = new Map(findings.findings.map((f) => [f.id, f] as const));
  const add = (source: WordingSource, text: string, areaId: string | null, itemId: string | null, findingId: string | null) => {
    if (!containsChinese(text)) return;
    if (itemId !== null && alreadyUnsynced.has(itemId) && source === 'reportSentence') return;
    const key = [source, areaId ?? '', itemId ?? '', findingId ?? '', text].join('\u0000');
    if (seen.has(key)) return;
    seen.add(key);
    const area = areaId !== null ? (areaById.get(areaId) ?? null) : null;
    const item = itemId !== null ? (itemById.get(itemId) ?? null) : null;
    const finding = findingId !== null ? (findingById.get(findingId) ?? null) : null;
    out.push(gap('clientEnglishChinese', true, area, item, finding, source, text.trim()));
  };
  for (const entry of reportBoundTextOf(inspectionId, inspection, findings, facts.captionOf ?? (() => ''))) {
    // a row's sentence is checked below as the report builds it (a stored sentence that no longer
    // stands is not what prints)
    if (entry.source === 'reportSentence') continue;
    add(entry.source, entry.text, entry.areaId, entry.itemId, entry.findingId);
  }
  for (const item of items) {
    if (item.status !== 'checkedOk' && item.status !== 'issueFound') continue;
    const sentence = rowSentenceFor('en-US', item, 'area', { photoNotes: facts.photoNotes(item), aiObservations: facts.aiObservations(item.id), advancedReview: facts.advancedReview(item.id) });
    add('reportSentence', sentence, item.areaId, item.id, null);
    if (item.status === 'issueFound') for (const note of facts.photoNotes(item)) add('photoCaption', note, item.areaId, item.id, null);
  }
  return out;
}

export function buildCloseout(inspectionId: string, inspection: InspectionState, findings: FindingState, facts: RowFactsInput = NO_FACTS): CloseoutSummary {
  const areas = inspection.areas.filter((a) => a.inspectionId === inspectionId && a.enabled).sort((a, b) => a.sortOrder - b.sortOrder);
  const gaps: CloseoutGap[] = [];
  const enabledAreaIds = new Set(areas.map((a) => a.id));
  const items = inspection.items.filter((i) => i.inspectionId === inspectionId && enabledAreaIds.has(i.areaId));
  const confirmed = findings.findings.filter((f) => f.inspectionId === inspectionId && f.status === 'confirmed' && enabledAreaIds.has(f.areaId));
  const unsynced = new Set<string>();

  for (const area of areas) {
    const areaItems = items.filter((i) => i.areaId === area.id).sort((a, b) => a.sortOrder - b.sortOrder);
    for (const item of areaItems) {
      if (item.status === 'notChecked') gaps.push(gap('uninspected', true, area, item, null));
      else if (itemResultIncomplete(item)) gaps.push(gap(item.reasonCode === null ? 'unableNoReason' : 'unableNoNote', true, area, item, null));
      else if (item.status === 'issueFound') {
        if (!defectRowHasFacts(item, facts)) gaps.push(gap('defectNoFacts', true, area, item, null));
        else if (!defectRowWordingWritten(item, facts)) gaps.push(gap('defectNoWording', false, area, item, null));
      }
      if (rowEnglishWordingNotSynced(item, facts)) {
        gaps.push(gap('wordingEnglishNotSynced', true, area, item, null));
        unsynced.add(item.id);
      }
      // a limited portion lives on an observed (or still uninspected) row, independently of its result
      if (itemLimitationsIncomplete(item)) gaps.push(gap('portionNoNote', true, area, item, null));
      // 2026-10-27: an AI-placed photo is a link, not a pending task — no closeout gap for it any more.
    }
    for (const finding of confirmed.filter((f) => f.areaId === area.id)) {
      if (finding.findingClass === 'informational') continue;
      if (finding.recommendation.trim().length === 0) gaps.push(gap('findingNoRecommendation', true, area, null, finding));
      if ((finding.findingClass === 'defect' || finding.findingClass === 'safety') && !responsibleTradeComplete(finding.responsibleTrade, finding.responsibleTradeOther)) {
        gaps.push(gap('findingNoTrade', true, area, null, finding));
      }
      if (finding.evidenceIds.length === 0) gaps.push(gap('findingNoPhoto', false, area, null, finding));
    }
  }

  gaps.push(...clientEnglishChineseGaps(inspectionId, inspection, findings, facts, areas, items, unsynced));

  const blocking = gaps.filter((g) => g.blocking);
  return {
    gaps,
    blocking,
    advisory: gaps.filter((g) => !g.blocking),
    progress: progressFor(items),
    enabledAreas: areas.length,
    canFinalize: areas.length > 0 && blocking.length === 0
  };
}
